import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import './FormulaExplanationLesson.css';


/*
  Momentan folosim clasele CSS existente din chaptercontainer.css.
  Dupa ce componenta functioneaza, stilurile pot fi mutate intr-un
  FormulaExplanationLesson.css separat.
*/

const normalizeFormulaOptionKey = (key) =>
  String(key || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const getExplanationItemHtml = (explanationItem) => {
  if (typeof explanationItem === 'string') {
    return explanationItem;
  }

  if (
    !explanationItem ||
    typeof explanationItem !== 'object'
  ) {
    return '';
  }

  return (
    explanationItem.html ||
    explanationItem.description ||
    explanationItem.descriptionExplanation ||
    explanationItem.text ||
    ''
  );
};

const getFormulaExplanationRawOptions = (
  explanationItem,
  question,
  explanationIndex
) => {
  const descriptionItems = Array.isArray(
    question?.descriptionExplanation
  )
    ? question.descriptionExplanation
    : [];

  const isLastDescription =
    explanationIndex === descriptionItems.length - 1;

  return (
    explanationItem?.options ??
    explanationItem?.formulaOptions ??
    (
      isLastDescription
        ? question?.options ?? question?.formulaOptions
        : null
    )
  );
};

const splitFormulaSideAccounts = (rawValue) => {
  if (
    rawValue === null ||
    rawValue === undefined
  ) {
    return [];
  }

  if (Array.isArray(rawValue)) {
    return rawValue
      .flatMap((item) =>
        splitFormulaSideAccounts(item)
      )
      .filter(Boolean);
  }

  if (typeof rawValue === 'object') {
    const objectAccount =
      rawValue.account ??
      rawValue.cont ??
      rawValue.name ??
      rawValue.label ??
      null;

    if (
      objectAccount === null ||
      objectAccount === undefined
    ) {
      return [];
    }

    const normalized = String(
      objectAccount
    ).trim();

    return normalized ? [normalized] : [];
  }

  const value = String(rawValue).trim();

  if (!value) {
    return [];
  }

  const parts = value
    .split(/\s*\/\s*|\s*;\s*|\s*\|\s*|\n+/)
    .map((item) => item.trim())
    .filter(
      (item) =>
        item !== '' &&
        item !== '%'
    );

  return parts.length > 0 ? parts : [value];
};

const getFormulaExplanationSides = (
  explanationItem,
  question,
  explanationIndex
) => {
  const rawOptions =
    getFormulaExplanationRawOptions(
      explanationItem,
      question,
      explanationIndex
    );

  if (!rawOptions) {
    return {
      leftAccounts: [],
      rightAccounts: [],
      operator: '=',
    };
  }

  if (Array.isArray(rawOptions)) {
    const left =
      rawOptions[0] !== undefined
        ? [String(rawOptions[0]).trim()]
        : [];

    const operator =
      rawOptions[1] !== undefined
        ? String(rawOptions[1]).trim()
        : '=';

    const right =
      rawOptions[2] !== undefined
        ? [String(rawOptions[2]).trim()]
        : [];

    return {
      leftAccounts: left.filter(Boolean),
      rightAccounts: right.filter(Boolean),
      operator: operator || '=',
    };
  }

  if (typeof rawOptions !== 'object') {
    return {
      leftAccounts: [],
      rightAccounts: [],
      operator: '=',
    };
  }

  const explicitSides =
    rawOptions.sides &&
    typeof rawOptions.sides === 'object'
      ? rawOptions.sides
      : null;

  const getByAliases = (aliases) => {
    const optionMatch = Object.entries(
      rawOptions
    ).find(([key]) =>
      aliases.includes(
        normalizeFormulaOptionKey(key)
      )
    );

    if (optionMatch) {
      return optionMatch[1];
    }

    if (explicitSides) {
      const sideMatch = Object.entries(
        explicitSides
      ).find(([key]) =>
        aliases.includes(
          normalizeFormulaOptionKey(key)
        )
      );

      if (sideMatch) {
        return sideMatch[1];
      }
    }

    return null;
  };

  const leftRaw = getByAliases([
    'cont1',
    'contul1',
    'account1',
    'leftaccount',
    'left',
    'debit',
    'debitaccounts',
    'leftaccounts',
  ]);

  const rightRaw = getByAliases([
    'cont2',
    'contul2',
    'account2',
    'rightaccount',
    'right',
    'credit',
    'creditaccounts',
    'rightaccounts',
  ]);

  const operatorRaw = getByAliases([
    'operator',
    'semn',
    'sign',
    'equals',
    'egal',
    'eq',
  ]);

  return {
    leftAccounts:
      splitFormulaSideAccounts(leftRaw),

    rightAccounts:
      splitFormulaSideAccounts(rightRaw),

    operator:
      String(operatorRaw ?? '=').trim() || '=',
  };
};

const getFormulaExplanationParts = (
  explanationItem,
  question,
  explanationIndex
) => {
  const rawOptions =
    getFormulaExplanationRawOptions(
      explanationItem,
      question,
      explanationIndex
    );

  if (Array.isArray(rawOptions)) {
    return rawOptions
      .filter((part) => {
        if (
          part === null ||
          part === undefined
        ) {
          return false;
        }

        if (typeof part === 'object') {
          return false;
        }

        return String(part).trim() !== '';
      })
      .map(String);
  }

  if (
    !rawOptions ||
    typeof rawOptions !== 'object'
  ) {
    return [];
  }

  const ignoredKeys = new Set([
    'rows',
    'formularows',
    'compoundrows',
    'entries',
    'lines',
    'breakdown',
    'detalii',
    'detaliere',
  ]);

  const entries = Object.entries(
    rawOptions
  ).filter(([key, value]) => {
    if (
      value === null ||
      value === undefined
    ) {
      return false;
    }

    if (typeof value === 'object') {
      return false;
    }

    if (
      ignoredKeys.has(
        normalizeFormulaOptionKey(key)
      )
    ) {
      return false;
    }

    return String(value).trim() !== '';
  });

  const groupedKeys = [
    [
      'cont1',
      'contul1',
      'account1',
      'leftaccount',
      'left',
      'debit',
    ],
    [
      'operator',
      'semn',
      'sign',
      'equals',
      'egal',
      'eq',
    ],
    [
      'cont2',
      'contul2',
      'account2',
      'rightaccount',
      'right',
      'credit',
    ],
    [
      'suma',
      'sum',
      'amount',
      'valoare',
    ],
  ];

  const usedKeys = new Set();
  const orderedParts = [];

  groupedKeys.forEach((keyGroup) => {
    const match = entries.find(([key]) =>
      keyGroup.includes(
        normalizeFormulaOptionKey(key)
      )
    );

    if (!match) {
      return;
    }

    usedKeys.add(match[0]);
    orderedParts.push(String(match[1]));
  });

  entries.forEach(([key, value]) => {
    if (!usedKeys.has(key)) {
      orderedParts.push(String(value));
    }
  });

  return orderedParts;
};

const getFormulaExplanationRows = (
  explanationItem,
  question,
  explanationIndex
) => {
  const rawOptions =
    getFormulaExplanationRawOptions(
      explanationItem,
      question,
      explanationIndex
    );

  if (
    !rawOptions ||
    typeof rawOptions !== 'object'
  ) {
    return [];
  }

  const rowKeys = [
    'rows',
    'formulaRows',
    'compoundRows',
    'entries',
    'lines',
    'breakdown',
    'detalii',
    'detaliere',
  ];

  const rowCollection = rowKeys
    .map((key) => rawOptions[key])
    .find(Array.isArray);

  if (!Array.isArray(rowCollection)) {
    return [];
  }

  return rowCollection
    .map((row) => {
      if (typeof row === 'string') {
        const value = row.trim();

        return value
          ? {
              account: value,
              amount: '',
            }
          : null;
      }

      if (
        !row ||
        typeof row !== 'object'
      ) {
        return null;
      }

      const account =
        row.cont ??
        row.account ??
        row.accountCode ??
        row.code ??
        row.contul ??
        '';

      const amount =
        row.suma ??
        row.amount ??
        row.value ??
        row.valoare ??
        '';

      const normalizedAccount =
        String(account).trim();

      const normalizedAmount =
        String(amount).trim();

      if (
        !normalizedAccount &&
        !normalizedAmount
      ) {
        return null;
      }

      return {
        account: normalizedAccount,
        amount: normalizedAmount,
      };
    })
    .filter(Boolean);
};


const FormulaCard = ({ question }) => {
  const descriptions = Array.isArray(
    question?.descriptionExplanation
  )
    ? question.descriptionExplanation
    : [];

  const lastDescriptionIndex = Math.max(
    descriptions.length - 1,
    0
  );

  const formulaParts =
    getFormulaExplanationParts(
      question,
      question,
      lastDescriptionIndex
    );

  const formulaRows =
    getFormulaExplanationRows(
      question,
      question,
      lastDescriptionIndex
    );

  const formulaSides =
    getFormulaExplanationSides(
      question,
      question,
      lastDescriptionIndex
    );

  const hasCompoundSides =
    formulaSides.leftAccounts.length > 0 &&
    formulaSides.rightAccounts.length > 0;

  const hasMultipleAccounts =
    formulaSides.leftAccounts.length > 1 ||
    formulaSides.rightAccounts.length > 1;

  const hasMainFormula =
    formulaParts.length >= 3;

  return (
    <div className="text-size-emplanation-bc">
      <div className="formula-explanation-separate-panel-bc">
        <div className="formula-title">
          Formula contabila
        </div>

        {formulaRows.length > 0 &&
        hasMainFormula ? (
          <div className="formula-explanation-expanded-layout-bc">
            <div className="formula-explanation-row">
              <span className="formula-explanation-token">
                {formulaParts[0]}
              </span>

              <span className="formula-explanation-token formula-explanation-token-operator">
                {formulaParts[1] || '='}
              </span>

              <span className="formula-explanation-token">
                {formulaParts[2]}
              </span>

              {formulaParts[3] && (
                <span className="formula-explanation-token">
                  {formulaParts[3]}
                </span>
              )}
            </div>

          <div
  className={`formula-explanation-accounts-column ${
    formulaParts[0] === '%'
      ? 'formula-rows-left'
      : formulaParts[2] === '%'
        ? 'formula-rows-right'
        : ''
  }`}
>
              {formulaRows.map(
                (row, rowIndex) => (
                  <div
                    key={`formula-row-${rowIndex}`}
                    className="formula-explanation-account-item"
                  >
                    <span className="accounting-note-account-cf">
                      {row.account}
                    </span>

                    {row.amount && (
                      <span className="accounting-note-amount-cf">
                        {row.amount}
                      </span>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        ) : hasCompoundSides &&
          hasMultipleAccounts ? (
          <div className="formula-explanation-compound-layout-bc">
            <div className="formula-explanation-compound-side-bc">
              {formulaSides.leftAccounts.map(
                (account, accountIndex) => (
                  <span
                    key={`left-${accountIndex}`}
                    className="formula-explanation-compound-account-bc"
                  >
                    {account}
                  </span>
                )
              )}
            </div>

            <span className="formula-explanation-token formula-explanation-token-operator formula-explanation-compound-operator-bc">
              {formulaSides.operator || '='}
            </span>

            <div className="formula-explanation-compound-side-bc">
              {formulaSides.rightAccounts.map(
                (account, accountIndex) => (
                  <span
                    key={`right-${accountIndex}`}
                    className="formula-explanation-compound-account-bc"
                  >
                    {account}
                  </span>
                )
              )}
            </div>
          </div>
        ) : hasMainFormula ? (
          <div className="formula-explanation-row">
            <span className="formula-explanation-token">
              {formulaParts[0]}
            </span>

            <span className="formula-explanation-token formula-explanation-token-operator">
              {formulaParts[1] || '='}
            </span>

            <span className="formula-explanation-token">
              {formulaParts[2]}
            </span>

            {formulaParts[3] && (
              <span className="formula-explanation-token">
                {formulaParts[3]}
              </span>
            )}
          </div>
        ) : formulaRows.length > 0 ? (
          <div className="formula-explanation-accounts-column">
            {formulaRows.map(
              (row, rowIndex) => (
                <div
                  key={`formula-row-${rowIndex}`}
                  className="formula-explanation-account-item"
                >
                  <span className="accounting-note-account-cf">
                    {row.account}
                  </span>

                  {row.amount && (
                    <span className="accounting-note-amount-cf">
                      {row.amount}
                    </span>
                  )}
                </div>
              )
            )}
          </div>
        ) : (
          <div>
            Formula nu este disponibila.
          </div>
        )}
      </div>
    </div>
  );
};

const FormulaExplanationLesson = ({
  question,
  cardIndex = 0,
  onCardIndexChange,
  onUnlockContinue,
}) => {
  const descriptions = Array.isArray(
    question?.descriptionExplanation
  )
    ? question.descriptionExplanation
    : [];

  /*
    Cardurile sunt:
    1. toate elementele descriptionExplanation;
    2. cardul final cu formula.
  */
  const totalCards = descriptions.length + 1;

  const [leavingCardIndex, setLeavingCardIndex] =
    useState(null);

  const [
    cardSwipeDirection,
    setCardSwipeDirection,
  ] = useState('left');

  const [
    cardDragOffsetX,
    setCardDragOffsetX,
  ] = useState(0);

  const [
    isCardDragging,
    setIsCardDragging,
  ] = useState(false);

  const leaveCardTimerRef = useRef(null);
  const internalChangeRef = useRef(false);
  const previousCardIndexRef =
    useRef(cardIndex);

  const cardSwipeRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    hasSwiped: false,
  });



  useEffect(() => {
  setLeavingCardIndex(null);
  setCardSwipeDirection('left');
  setCardDragOffsetX(0);
  setIsCardDragging(false);

  previousCardIndexRef.current = 0;
  onCardIndexChange?.(0);
}, [
  question,
  totalCards,
  onCardIndexChange,
]);



useEffect(() => {
  if (totalCards <= 1) {
    onUnlockContinue?.();
  }
}, [
  totalCards,
  onUnlockContinue,
]);



  /*
    Acest efect permite butonului extern din
    LessonActionBar sa schimbe cardul si sa pastreze animatia.
  */
  useEffect(() => {
    const previousIndex =
      previousCardIndexRef.current;

    if (previousIndex === cardIndex) {
      return;
    }

    if (internalChangeRef.current) {
      internalChangeRef.current = false;
      previousCardIndexRef.current = cardIndex;
      return;
    }

    const isForward =
      cardIndex ===
      (previousIndex + 1) % totalCards;

    setCardSwipeDirection(
      isForward ? 'left' : 'right'
    );

    setLeavingCardIndex(previousIndex);
    setCardDragOffsetX(0);
    setIsCardDragging(false);

    if (leaveCardTimerRef.current) {
      clearTimeout(leaveCardTimerRef.current);
    }

    leaveCardTimerRef.current = setTimeout(() => {
      setLeavingCardIndex(null);
      leaveCardTimerRef.current = null;
    }, 360);

    previousCardIndexRef.current = cardIndex;
  }, [cardIndex, totalCards]);

  useEffect(() => {
    if (
      totalCards > 0 &&
      cardIndex === totalCards - 1
    ) {
      onUnlockContinue?.();
    }
  }, [
    cardIndex,
    totalCards,
    onUnlockContinue,
  ]);

  useEffect(() => {
    return () => {
      if (leaveCardTimerRef.current) {
        clearTimeout(
          leaveCardTimerRef.current
        );
      }
    };
  }, []);

  const updateCardIndexWithSwipe = useCallback(
    (nextIndex, direction = 'left') => {
      if (
        nextIndex === cardIndex ||
        totalCards <= 1
      ) {
        return;
      }

      internalChangeRef.current = true;

      setCardSwipeDirection(
        direction === 'right'
          ? 'right'
          : 'left'
      );

      setCardDragOffsetX(0);
      setIsCardDragging(false);
      setLeavingCardIndex(cardIndex);

      if (leaveCardTimerRef.current) {
        clearTimeout(
          leaveCardTimerRef.current
        );
      }

      previousCardIndexRef.current =
        nextIndex;

      onCardIndexChange?.(nextIndex);

      leaveCardTimerRef.current =
        setTimeout(() => {
          setLeavingCardIndex(null);
          leaveCardTimerRef.current = null;
        }, 360);
    },
    [
      cardIndex,
      totalCards,
      onCardIndexChange,
    ]
  );

  const resetCardSwipe = useCallback(() => {
    setCardDragOffsetX(0);
    setIsCardDragging(false);

    cardSwipeRef.current = {
      active: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0,
      hasSwiped: false,
    };
  }, []);

  const commitCardSwipe = useCallback(() => {
    const swipeState =
      cardSwipeRef.current;

    if (
      !swipeState.active ||
      swipeState.hasSwiped ||
      totalCards <= 1
    ) {
      return;
    }

    const deltaX =
      swipeState.lastX -
      swipeState.startX;

    const deltaY =
      swipeState.lastY -
      swipeState.startY;

    if (
      Math.abs(deltaX) < 42 ||
      Math.abs(deltaX) <= Math.abs(deltaY)
    ) {
      return;
    }

    swipeState.hasSwiped = true;

    const direction =
      deltaX < 0 ? 'left' : 'right';

    const nextIndex =
      direction === 'left'
        ? (cardIndex + 1) % totalCards
        : (
            cardIndex -
            1 +
            totalCards
          ) % totalCards;

    updateCardIndexWithSwipe(
      nextIndex,
      direction
    );
  }, [
    cardIndex,
    totalCards,
    updateCardIndexWithSwipe,
  ]);

  const handlePointerDown = useCallback(
    (event) => {
      if (
        event.pointerType === 'mouse' &&
        event.button !== 0
      ) {
        return;
      }

      cardSwipeRef.current = {
        active: true,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        hasSwiped: false,
      };

      setCardDragOffsetX(0);
      setIsCardDragging(false);

      event.currentTarget
        ?.setPointerCapture?.(
          event.pointerId
        );
    },
    []
  );

  const handlePointerMove = useCallback(
    (event) => {
      const swipeState =
        cardSwipeRef.current;

      if (
        !swipeState.active ||
        swipeState.hasSwiped
      ) {
        return;
      }

      if (
        swipeState.pointerId !== null &&
        event.pointerId !==
          swipeState.pointerId
      ) {
        return;
      }

      swipeState.lastX = event.clientX;
      swipeState.lastY = event.clientY;

      const deltaX =
        event.clientX -
        swipeState.startX;

      const deltaY =
        event.clientY -
        swipeState.startY;

      if (
        Math.abs(deltaX) <=
        Math.abs(deltaY)
      ) {
        return;
      }

      const clampedOffset = Math.max(
        -150,
        Math.min(150, deltaX)
      );

      setIsCardDragging(true);
      setCardDragOffsetX(clampedOffset);
    },
    []
  );

  const handlePointerUp = useCallback(
    (event) => {
      const swipeState =
        cardSwipeRef.current;

      if (!swipeState.active) {
        return;
      }

      if (
        swipeState.pointerId !== null &&
        event.pointerId !==
          swipeState.pointerId
      ) {
        return;
      }

      swipeState.lastX = event.clientX;
      swipeState.lastY = event.clientY;

      commitCardSwipe();

      try {
        event.currentTarget
          ?.releasePointerCapture?.(
            event.pointerId
          );
      } catch {
        // Pointer-ul poate fi deja eliberat.
      }

      resetCardSwipe();
    },
    [
      commitCardSwipe,
      resetCardSwipe,
    ]
  );

  const getCardStyle = (
    index,
    offset,
    isLeavingCard
  ) => {
    let transform;

    if (isLeavingCard) {
      transform =
        cardSwipeDirection === 'right'
          ? 'translateX(120%) rotate(7deg) scale(0.93)'
          : 'translateX(-120%) rotate(-7deg) scale(0.93)';
    } else if (offset === 0) {
      transform = `translateX(${
        isCardDragging
          ? cardDragOffsetX
          : 0
      }px) rotate(${
        isCardDragging
          ? cardDragOffsetX / 24
          : 0
      }deg) scale(0.95)`;
    } else {
      transform = `translateY(0) translateX(${
        isCardDragging
          ? cardDragOffsetX * 0.16
          : 0
      }px) scale(0.96) rotate(2deg)`;
    }

    return {
      transform,
      zIndex: isLeavingCard
        ? 12
        : 10 - offset,
      opacity: isLeavingCard
        ? 0
        : offset === 0
          ? 1
          : 0.5,
    };
  };

  if (!question) {
    return null;
  }

  return (
    <div className="quiz-content-scroll-bc quiz-content-scroll-fixed-bc">
      <div className="description-lesson-text-explanation-bc">
        <div
          className="card-stack-container"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={resetCardSwipe}
        >
          {descriptions.map(
            (explanation, index) => {
              const offset =
                (
                  index -
                  cardIndex +
                  totalCards
                ) % totalCards;

              const isLeavingCard =
                index === leavingCardIndex;

              if (
                offset > 1 &&
                !isLeavingCard
              ) {
                return null;
              }

              const cardStateClass =
                isLeavingCard
                  ? 'leaving'
                  : offset === 0
                    ? 'active'
                    : 'next';

              const explanationHtml =
                getExplanationItemHtml(
                  explanation
                );

              return (
                <div
                  key={`description-${index}`}
                  className={`explanation-card-bc ${cardStateClass}`}
                  style={getCardStyle(
                    index,
                    offset,
                    isLeavingCard
                  )}
                >
                  <div className="text-size-emplanation-bc">
                    {explanationHtml && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            explanationHtml,
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            }
          )}

          {(() => {
            const formulaIndex =
              descriptions.length;

            const offset =
              (
                formulaIndex -
                cardIndex +
                totalCards
              ) % totalCards;

            const isLeavingCard =
              formulaIndex ===
              leavingCardIndex;

            if (
              offset > 1 &&
              !isLeavingCard
            ) {
              return null;
            }

            const cardStateClass =
              isLeavingCard
                ? 'leaving'
                : offset === 0
                  ? 'active'
                  : 'next';

            return (
              <div
                key="formula-card"
                className={`explanation-card-bc ${cardStateClass}`}
                style={getCardStyle(
                  formulaIndex,
                  offset,
                  isLeavingCard
                )}
              >
                <FormulaCard
                  question={question}
                />
              </div>
            );
          })()}
        </div>

        {question.image && (
          <div className="lesson-image-container">
            <img
              src={question.image}
              alt={
                question.titleExplanation ||
                'Explicatie formula'
              }
              className="lesson-image"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FormulaExplanationLesson;